import React, { useRef, useState } from 'react';
import { styles } from './styles';

interface ReflectionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Colour of the cursor-following highlight. */
  highlightColor?: string;
  /** Diameter of the highlight, in px. */
  highlightSize?: number;
}

/**
 * A drop-in wrapper around <button>. Identical to a normal button except that,
 * on hover, a soft radial highlight tracks the cursor across its surface.
 * All native button props (onClick, type, disabled, style, aria-*, …) pass through.
 */
const ReflectionButton: React.FC<ReflectionButtonProps> = ({
  children,
  style,
  disabled,
  highlightColor = 'rgba(255,255,255,0.22)',
  highlightSize = 130,
  onMouseMove,
  onMouseEnter,
  onMouseLeave,
  ...rest
}) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [hovered, setHovered] = useState(false);
  const [pos, setPos] = useState({ x: -9999, y: -9999 });

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = ref.current?.getBoundingClientRect();
    if (rect) setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    onMouseMove?.(e);
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    setHovered(true);
    onMouseEnter?.(e);
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    setHovered(false);
    onMouseLeave?.(e);
  };

  return (
    <button
      ref={ref}
      disabled={disabled}
      style={{ ...style, ...styles.button }}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...rest}
    >
      <span
        aria-hidden
        style={{
          ...styles.reflection,
          opacity: hovered && !disabled ? 1 : 0,
          background: `radial-gradient(${highlightSize}px circle at ${pos.x}px ${pos.y}px, ${highlightColor}, transparent 70%)`,
        }}
      />
      <span style={styles.content}>{children}</span>
    </button>
  );
};

export default ReflectionButton;
